import { useAgentConfig } from '@/hooks/useAgentConfig';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';

export function AgentConfigPanel() {
  const {
    options,
    config,
    loading,
    error,
    toggleCorpus,
    toggleGoogleSearch,
    setModel
  } = useAgentConfig();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">Loading configuration...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-4">
          <p className="text-destructive">Error: {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!options) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent Configuration</CardTitle>
        <CardDescription>
          Customize your agent's capabilities
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Model Selection */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Model</Label>
          <RadioGroup value={config.model} onValueChange={setModel}>
            {options.models.map(model => (
              <div key={model.id} className="flex items-start space-x-3 space-y-0">
                <RadioGroupItem value={model.id} id={model.id} />
                <div className="space-y-1 leading-none">
                  <Label htmlFor={model.id} className="font-medium cursor-pointer">
                    {model.name}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {model.description}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Knowledge Sources */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Knowledge Sources</Label>
          <div className="space-y-3">
            {options.corpora.map(corpus => (
              <div key={corpus.corpus_id} className="flex items-start space-x-3">
                <Checkbox
                  id={corpus.corpus_id}
                  checked={config.corpus_ids.includes(corpus.corpus_id)}
                  onCheckedChange={() => toggleCorpus(corpus.corpus_id)}
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor={corpus.corpus_id} className="font-medium cursor-pointer">
                    {corpus.name}
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({corpus.type})
                    </span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {corpus.description}
                  </p>
                  {corpus.file_count && (
                    <p className="text-xs text-muted-foreground">
                      {corpus.file_count} files
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Tools */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Additional Tools</Label>
          <div className="flex items-start space-x-3">
            <Checkbox
              id="google-search"
              checked={config.enable_google_search}
              onCheckedChange={toggleGoogleSearch}
            />
            <div className="space-y-1 leading-none">
              <Label htmlFor="google-search" className="font-medium cursor-pointer">
                Google Search
              </Label>
              <p className="text-sm text-muted-foreground">
                Search the web for current information
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            <strong>Active:</strong>{' '}
            {options.models.find(m => m.id === config.model)?.name || 'Normal'} model,{' '}
            {config.corpus_ids.length} knowledge source(s),{' '}
            {config.enable_google_search ? 'with' : 'without'} web search
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
